import React from 'react';
import { useLocation } from 'react-router-dom';

import Modal from 'antd/es/modal';
import once from 'lodash/once';

import { ItemsProvider } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import SimpleSearchInput from '@common/react/components/Forms/SimpleSearchInput/SimpleSearchInput';
import Loader from '@common/react/components/Core/LoaderProvider/Loader';
import SearchMenu from '@common/react/components/UI/SearchModal/SearchMenu';
import { MenuItem } from '@common/react/objects/MenuItem';
import { SearchObject, CustomItemDelegate } from '@common/react/components/UI/SearchModal/index';
import { getLocalStorageValue } from '@common/react/utils/localStorage/localStorage';
import { useApplicationContext } from '@common/react/components/Core/Application/Application';

import '@common/react/scss/components/searchModal.scss';

export interface SearchModalContext {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export const createSearchModalContext = once(() => React.createContext({} as SearchModalContext));

export const useSearchModalContext: () => SearchModalContext = () => React.useContext(createSearchModalContext());

const CustomInput: React.FC<any> = ({ open, ...props }) => {
	const ref = React.useRef<HTMLInputElement>(null);

	React.useEffect(() => {
		if (open) {
			setTimeout(() => ref.current?.focus(), 0);
		}
	}, [open]);

	return <input
		{...props}
		ref={ref}
	/>;
};

interface SearchModalProps<T extends number> {
	menuItems: Array<MenuItem>;
	enable?: boolean;
	customItem?: CustomItemDelegate<T>;
	getPathFromType: (type: T) => string;
	allowTransmutation?: boolean;
	transformItems?: (items: Array<SearchObject<T>>) => Array<SearchObject<T>>;
	loadRequest?: string | null;
	additional?: (item: SearchObject<T>) => React.ReactNode;
	children: any;
}

/**
 * SearchModal component
 *
 * expect T is some SearchObjectType enum with Menu = 0, User = 1,
 */
const SearchModal: <T extends number>(
	p: SearchModalProps<T>
) => React.ReactElement<T> = <T extends number>(
	props: SearchModalProps<T>,
) => {
	const {
		children,
		enable,
		menuItems,
		customItem,
		getPathFromType,
		allowTransmutation = false,
		transformItems,
		loadRequest = 'searchType',
		additional,
	} = props;

	if (!enable) return <>{children}</>;

	const { getUser } = useApplicationContext();
	const user = getUser();
	const location = useLocation();
	const [open, setOpen] = React.useState(false);
	const lastItems: Array<SearchObject<T>> = React.useMemo(() => {
		return open ? JSON.parse(getLocalStorageValue(`${user?.id ?? ''}-search-modal-last-items`, '[]')) : [];
	}, [open]);
	const Context = createSearchModalContext();

	React.useEffect(() => {
		const keydown = (e: KeyboardEvent) => {
			if (e.code === 'KeyK' && (e.ctrlKey || e.metaKey)) {
				e.preventDefault();
				setOpen(true);
			}
		};
		document.addEventListener('keydown', keydown);
		return () => {
			document.removeEventListener('keydown', keydown);
		};
	}, []);

	React.useEffect(() => {
		setOpen(false);
	}, [location]);

	const CustomInputMemo = React.useMemo(() => {
		return (props) => <CustomInput {...props} open={open} />;
	}, [open]);

	return <Context.Provider value={{ open, setOpen }}>
		<Modal
			open={open}
			footer={null}
			zIndex={1200}
			closeIcon={null}
			width={700}
			className="search-modal"
			onCancel={() => setOpen(false)}
			destroyOnClose
		>
			<ItemsProvider<SearchObject<T>>
				delay={200}
				loadRequest={loadRequest}
				loadRequestExact
				filters={{
					text: '',
				}}
				skipInitLoad
				transformItems={transformItems}
			>
				{({ state: { items, loading, filters }, actions: { handleChange } }) => <>
					<div className="search-modal-control">
						<SimpleSearchInput
							onChange={(evt) => {
								if (evt.currentTarget.value !== filters.text) {
									handleChange(evt);
								}
							}}
							otherInputProps={{
								// eslint-disable-next-line
								// @ts-ignore
								autocomplete: 'off',
							}}
							inputComponent={CustomInputMemo}
						/>
					</div>
					<div className="is-relative search-modal-inner">
						<SearchMenu<T>
							allowTransmutation={allowTransmutation}
							getPathFromType={getPathFromType}
							customItem={customItem}
							open={open}
							loading={loading}
							search={filters.text ?? ''}
							searchItems={items}
							items={menuItems}
							lastItems={lastItems}
							additional={additional}
						/>
						{loading && <div className="spin-container"><Loader /></div>}
					</div>
				</>}
			</ItemsProvider>
		</Modal>
		{typeof children === 'function' ? children({ open, setOpen }) : children}
	</Context.Provider>;
};

export default SearchModal;
